<!-- One avatar, used by every row: a person's initials, or an icon for a channel, patient, referral or support
     (chat/core/avatar.ts). One place, so the rows cannot drift apart. -->
<ng-template #avatarTpl let-c>
  <span class="avatar" *ngIf="avatar(c) as a" [style.--hue]="a.hue">
    <img *ngIf="photo(c) as src; else drawn" [src]="src" alt="" loading="lazy" />
    <ng-template #drawn>
      <mat-icon *ngIf="a.icon as i">{{ i }}</mat-icon>
      <span class="initials" *ngIf="a.initials as ini">{{ ini }}</span>
    </ng-template>
  </span>
</ng-template>

<!--
  One row, as the referrals dashboard's chat list draws it (2026-09-22): the avatar with the red unread count on its
  corner (and a presence dot for a direct message), then the name and the time of the last message, and under them a
  one-line preview. The flags (muted, admin view, read-only) sit after the name.
-->
<ng-template #rowTpl let-c let-online="online" let-alias="alias">
  <span class="avatar-wrap">
    <ng-container *ngTemplateOutlet="avatarTpl; context: { $implicit: c }"></ng-container>
    <span class="badge" *ngIf="c.unread > 0 && (c.type !== 'channel' || c.access === 'participant')" [class.muted]="muted(c)">{{ c.unread > 99 ? '99+' : c.unread }}</span>
    <span class="presence" [class.on]="isOnline(c, online)" *ngIf="c.type === 'direct'"></span>
  </span>
  <span class="row-main">
    <span class="row-top">
      <span class="name" [attr.title]="networkEmail(c)">{{ title(c) }}<small *ngIf="alias"> · {{ alias }}</small></span>
      <mat-icon class="flag" *ngIf="c.readOnlyReason" [matTooltip]="c.type === 'billing' ? 'Read-only: no longer billing this practice' : 'Read-only: sharing stopped'">lock</mat-icon>
      <mat-icon class="flag" *ngIf="c.access === 'admin'" matTooltip="You see this as a practice admin">admin_panel_settings</mat-icon>
      <mat-icon class="flag" *ngIf="muted(c)" matTooltip="Muted">notifications_off</mat-icon>
      <mat-icon class="flag" *ngIf="c.pinnedAt" matTooltip="Pinned">push_pin</mat-icon>
      <span class="tag" *ngIf="tag(c) as t" [attr.data-tone]="tone(c)">{{ t }}</span>
      <span class="when" *ngIf="when(c) as w">{{ w }}</span>
    </span>
    <span class="preview">{{ c.lastMessagePreview || emptyPreview(c) }}</span>
  </span>
</ng-template>

<ng-container *ngIf="vm$ | async as vm">
  <div class="toolbar">
    <label class="search">
      <mat-icon>search</mat-icon>
      <input #searchBox placeholder="Search" [ngModel]="filter$.value" (ngModelChange)="onFilter($event)" aria-label="Find a conversation" />
      <button type="button" class="clear" *ngIf="filter$.value" (click)="onFilter(''); searchBox.focus()" aria-label="Clear search" matTooltip="Clear">
        <mat-icon>close</mat-icon>
      </button>
    </label>
    <!-- Notifications on this device (push) and message sounds: both per device, so they live here, not per conversation. -->
    <button mat-icon-button (click)="openNotifySettings()" matTooltip="Notifications and sounds" aria-label="Notifications and sounds">
      <mat-icon>{{ (push.state$ | async) === 'on' || (sound.enabled$ | async) ? 'notifications_active' : 'notifications_none' }}</mat-icon>
    </button>
    <button mat-icon-button (click)="startNew(vm.orgId!)" [disabled]="!vm.orgId" matTooltip="New conversation" aria-label="New conversation">
      <mat-icon>add_circle_outline</mat-icon>
    </button>
  </div>

  <!-- Filter chips: one kind of conversation at a time. Only drawn when there is more than one kind. -->
  <div class="chips" role="toolbar" aria-label="Show conversations" *ngIf="vm.ready && vm.orgId && vm.chips.length > 2">
    <button type="button" class="chip" *ngFor="let chip of vm.chips" (click)="setKind(chip.kind)"
      [class.on]="vm.kind === chip.kind" [attr.aria-pressed]="vm.kind === chip.kind">
      <span class="chip-label">{{ chip.label }}</span><span class="chip-count">({{ chip.count }})</span>
    </button>
  </div>

  <app-chat-conversation-menu #rowMenu xPosition="after"></app-chat-conversation-menu>
  <span #rowMenuAnchor class="row-menu-anchor" [matMenuTriggerFor]="rowMenu.menu" aria-hidden="true"></span>

  <div class="scroll">
    <div class="loading" *ngIf="!vm.ready"><mat-spinner diameter="26"></mat-spinner></div>
    <div class="empty" *ngIf="vm.ready && !vm.orgId">
      <mat-icon>forum</mat-icon>
      <strong>Chat is not available</strong>
      <span>Chat has not been set up for this practice yet.</span>
    </div>

    <ng-container *ngIf="vm.ready && vm.orgId">
      <!-- One list, newest message first, each row tagged with its kind. -->
      <!-- Right-click, or a long press on touch, opens the conversation's menu instead of the conversation. -->
      <button #row class="row" *ngFor="let c of vm.rows; trackBy: trackById" (click)="select(c)" [attr.data-tone]="tone(c)"
        (contextmenu)="openRowMenu(c, row, $event)" (appLongPress)="openRowMenu(c, row)"
        [class.about-open-patient]="aboutOpenPatient(c, vm.openPatientId)"
        [class.unread]="c.unread > 0 && !muted(c)" [class.looking]="c.type === 'channel' && c.access !== 'participant'">
        <ng-container *ngTemplateOutlet="rowTpl; context: { $implicit: c, online: vm.online, alias: c.type === 'network' ? networkAlias(c, vm.network) : null }"></ng-container>
      </button>
      <div class="empty small" *ngIf="vm.emptyText">{{ vm.emptyText }}</div>

      <ng-container *ngIf="hits.length || searching || searchFailed">
        <h3 class="section">Messages</h3>
        <div class="empty small" *ngIf="searching && !hits.length">Searching…</div>
        <div class="empty small" *ngIf="searchFailed">Messages could not be searched just now.</div>
        <button class="row hit" *ngFor="let h of hits" (click)="openHit(h)">
          <span class="avatar hit-icon"><mat-icon>search</mat-icon></span>
          <span class="row-main">
            <span class="row-top"><span class="name">{{ h.conversationName }}<small> · {{ h.senderName }}</small></span></span>
            <span class="preview">{{ h.snippet }}</span>
          </span>
        </button>
      </ng-container>
    </ng-container>
  </div>
</ng-container>
